import { computed, inject, provide, ref } from 'vue';
import type { ComputedRef, InjectionKey, Ref } from 'vue';
import { getUserInfo } from '@/api/requset/userLogin';

/** 巡检管理端角色访问状态。 */
export interface PatrolAccessState {
  /** 当前用户角色名称 */
  roleNames: Ref<string[]>;
  /** 角色信息是否正在加载 */
  accessLoading: Ref<boolean>;
  /** 当前用户是否为项目管理员 */
  isProjectManager: ComputedRef<boolean>;
  /** 当前用户是否为巡检模板维护员 */
  isTemplateManager: ComputedRef<boolean>;
  /** 当前用户是否允许进入巡检管理端 */
  canManagePatrol: ComputedRef<boolean>;
  /** 加载当前登录用户的角色信息 */
  loadAccess: () => Promise<void>;
}

/** 巡检管理端角色状态注入键。 */
const patrolAccessKey: InjectionKey<PatrolAccessState> = Symbol('patrolAccess');

/** 允许进入巡检管理端的角色名称集合。 */
const patrolManageRoleNames = new Set([
  'patrol_project_manager',
  'patrol_template_manager',
]);

/**
 * 判断角色名称列表是否拥有巡检管理端访问权限。
 *
 * @param roleNames - 当前用户角色名称列表
 * @returns 是否允许进入巡检管理端
 */
export const hasPatrolManageAccess = (roleNames: unknown): boolean => {
  return (
    Array.isArray(roleNames) && roleNames.some(roleName => patrolManageRoleNames.has(roleName))
  );
};

/**
 * 在巡检管理布局中创建并提供角色访问状态。
 *
 * @returns 巡检管理端角色访问状态
 */
export const providePatrolAccess = (): PatrolAccessState => {
  // 当前登录用户角色名称
  const roleNames = ref<string[]>([]);
  // 角色信息加载状态
  const accessLoading = ref(true);
  // 当前用户是否为项目管理员
  const isProjectManager = computed(() => roleNames.value.includes('patrol_project_manager'));
  // 当前用户是否为独立的巡检模板维护员
  const isTemplateManager = computed(() => roleNames.value.includes('patrol_template_manager'));
  // 当前用户是否允许使用巡检管理端
  const canManagePatrol = computed(() => hasPatrolManageAccess(roleNames.value));

  /**
   * 加载当前登录用户的角色信息。
   *
   * @returns 无返回值
   */
  const loadAccess = async () => {
    accessLoading.value = true;
    try {
      const response = await getUserInfo();
      roleNames.value = Array.isArray(response?.data?.roleNames) ? response.data.roleNames : [];
    } finally {
      accessLoading.value = false;
    }
  };

  const state: PatrolAccessState = {
    roleNames,
    accessLoading,
    isProjectManager,
    isTemplateManager,
    canManagePatrol,
    loadAccess,
  };
  provide(patrolAccessKey, state);
  return state;
};

/**
 * 获取巡检管理布局提供的角色访问状态。
 *
 * @returns 巡检管理端角色访问状态
 */
export const usePatrolAccess = (): PatrolAccessState => {
  const state = inject(patrolAccessKey);
  if (!state) throw new Error('usePatrolAccess 必须在巡检管理布局内使用');
  return state;
};
